/* Sprint 2Q — Admin ESG Dashboard panel.
 *
 * Tabs:
 *   1) Oversigt — totals + aggregates + recommendations
 *   2) Køretøjer — ESG vehicle registry CRUD
 *   3) Rapporter — customer-by-customer report download (PDF + XLSX)
 *
 * All wording is compliance-safe (no greenwashing claims).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2,
  Leaf,
  CheckCircle2,
  XCircle,
  Plus,
  Download,
  FileText,
  Truck,
  TrendingDown,
  RefreshCw,
  ShieldCheck,
  AlertTriangle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function adminApi() {
  const token = localStorage.getItem("ema_admin_token");
  return axios.create({
    baseURL: API,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    responseType: "json",
  });
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(url);
    a.remove();
  }, 100);
}

const FUEL_LABELS = {
  electric: "El",
  petrol: "Benzin",
  diesel: "Diesel",
  hybrid: "Hybrid",
};

const OWNER_LABELS = {
  ema: "EMA",
  driver: "Chauffør",
  subcontractor: "Vognmand",
};

function currentPeriod() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function buildPeriodOptions(months = 12) {
  const out = [];
  const seen = new Set();
  const d = new Date();
  for (let i = 0; i < months; i += 1) {
    const p = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    if (!seen.has(p)) {
      out.push(p);
      seen.add(p);
    }
    d.setMonth(d.getMonth() - 1);
  }
  return out;
}

// ============================================================
// ESG Panel
// ============================================================

export default function EsgPanel() {
  const [sub, setSub] = useState("dashboard");
  return (
    <div data-testid="esg-panel" className="space-y-4">
      <div className="flex items-center gap-2 border-b border-brand mb-2">
        {[
          { k: "dashboard", t: "Oversigt", Icon: Leaf },
          { k: "vehicles", t: "Køretøjer", Icon: Truck },
          { k: "reports", t: "Kunderapporter", Icon: FileText },
        ].map((s) => {
          const I = s.Icon;
          return (
            <button
              key={s.k}
              onClick={() => setSub(s.k)}
              data-testid={`esg-subtab-${s.k}`}
              className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px flex items-center gap-1.5 ${
                sub === s.k
                  ? "border-brand-red text-brand-red"
                  : "border-transparent text-brand-text2 hover:text-brand-dark"
              }`}
            >
              <I size={14} /> {s.t}
            </button>
          );
        })}
      </div>
      {sub === "dashboard" && <EsgDashboard />}
      {sub === "vehicles" && <EsgVehicles />}
      {sub === "reports" && <EsgCustomerReports />}
    </div>
  );
}

// ============================================================
// Dashboard
// ============================================================

function EsgDashboard() {
  const [period, setPeriod] = useState(currentPeriod());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi().get(
        `/admin/esg/dashboard?period=${period}`,
      );
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke hente ESG-data");
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    load();
  }, [load]);

  const runBackfill = async () => {
    if (!window.confirm("Genberegn CO2e for de seneste 500 ruter?")) return;
    try {
      const r = await adminApi().post("/admin/esg/backfill");
      toast.success(`Genberegnet ${r.data.processed} ruter`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backfill fejlede");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="esg-dashboard-loading">
        <Loader2 className="animate-spin text-brand-red" size={24} />
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="space-y-4" data-testid="esg-dashboard">
      <MotorApiStatusCard />
      {/* Compliance banner */}
      <div className="bg-amber-50 border-l-4 border-amber-500 p-3 text-xs text-amber-900 flex items-start gap-2">
        <ShieldCheck size={16} className="mt-0.5 shrink-0" />
        <span>
          <b>Compliance:</b> Værdier er operationelt dokumenterede estimater
          (EMA Kurer metode {data.method_version}). Ikke eksternt verificerede.
          Brug aldrig &quot;CO2-neutral&quot;, &quot;klima-neutral&quot; eller &quot;grøn levering&quot; i
          kundekommunikation.
        </span>
      </div>

      {/* Period selector + refresh */}
      <div className="flex flex-wrap items-center gap-3 bg-white p-3 border border-brand">
        <label className="text-xs uppercase tracking-wider text-brand-text2 font-semibold">
          Periode
        </label>
        <select
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          className="border border-brand px-3 py-1.5 text-sm bg-white"
          data-testid="esg-period-select"
        >
          {buildPeriodOptions(12).map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
        <button
          onClick={load}
          className="text-xs px-3 py-1.5 border border-brand text-brand-dark inline-flex items-center gap-1"
          data-testid="esg-refresh"
        >
          <RefreshCw size={12} /> Opdater
        </button>
        <button
          onClick={runBackfill}
          className="text-xs px-3 py-1.5 bg-brand-dark text-white inline-flex items-center gap-1 font-semibold"
          data-testid="esg-backfill"
        >
          Backfill CO2e (alle ruter)
        </button>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="esg-totals">
        <Kpi label="Ruter i perioden" value={data.totals.routes} />
        <Kpi label="Forsendelser" value={data.totals.shipments} />
        <Kpi
          label="Dokumenteret CO2e (kg)"
          value={data.totals.co2e_kg.toFixed(2)}
          subtle={`Metode ${data.method_version}`}
        />
      </div>

      {/* Aggregates */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <AggregateTable
          title="CO2e pr. kunde"
          rows={data.by_customer}
          testid="esg-by-customer"
          idLabel="Kunde-ID"
        />
        <AggregateTable
          title="CO2e pr. køretøj"
          rows={data.by_vehicle}
          testid="esg-by-vehicle"
          idLabel="Køretøj-ID"
        />
        <AggregateTable
          title="CO2e pr. service-niveau"
          rows={data.by_service_level}
          testid="esg-by-service"
          idLabel="Service"
        />
        <AggregateTable
          title="CO2e pr. vognmand"
          rows={data.by_subcontractor}
          testid="esg-by-subcontractor"
          idLabel="Vognmand-ID"
        />
      </div>

      {/* Density best/worst + high emission */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <DensityTable
          title="Bedste pakketæthed (top 5)"
          rows={data.best_density_routes}
          testid="esg-best-density"
        />
        <DensityTable
          title="Dårligste pakketæthed (bund 5)"
          rows={data.worst_density_routes}
          testid="esg-worst-density"
        />
      </div>

      <div className="bg-white border border-brand p-4" data-testid="esg-high-emission">
        <div className="text-xs uppercase tracking-widest text-brand-text2 font-bold mb-2">
          Højeste-emissions ruter (top 10)
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-brand-text2 border-b border-brand">
                <th className="text-left py-1.5 pr-2">Rute-ID</th>
                <th className="text-right py-1.5 pr-2">Forsendelser</th>
                <th className="text-right py-1.5">CO2e (kg)</th>
              </tr>
            </thead>
            <tbody>
              {data.high_emission_routes.length === 0 && (
                <tr><td colSpan={3} className="text-center py-3 text-brand-text2">Ingen data</td></tr>
              )}
              {data.high_emission_routes.map((r) => (
                <tr key={r._id} className="border-b border-brand last:border-0">
                  <td className="py-1.5 pr-2 font-mono">{(r._id || "—").slice(0, 16)}</td>
                  <td className="py-1.5 pr-2 text-right">{r.shipments}</td>
                  <td className="py-1.5 text-right font-semibold">{(r.co2e_kg || 0).toFixed(3)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recommendations */}
      <div className="bg-white border border-brand p-4" data-testid="esg-recommendations">
        <div className="text-xs uppercase tracking-widest text-brand-text2 font-bold mb-2 flex items-center gap-2">
          <TrendingDown size={14} /> Operationelle anbefalinger
        </div>
        {data.recommendations.length === 0 ? (
          <div className="text-sm text-brand-text2">
            Ingen anbefalinger i denne periode.
          </div>
        ) : (
          <ul className="space-y-2 text-sm">
            {data.recommendations.map((r, i) => (
              <li
                key={i}
                className="border-l-4 border-brand-red bg-brand-bg2 px-3 py-2"
                data-testid={`esg-rec-${r.kind}`}
              >
                <div className="font-semibold text-brand-dark">{r.title}</div>
                <div className="text-xs text-brand-text2 mt-0.5">{r.rationale}</div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="text-[11px] text-brand-text2 italic px-1">
        {data.disclaimer}
      </div>
    </div>
  );
}

function Kpi({ label, value, subtle }) {
  return (
    <div className="bg-white border border-brand p-4">
      <div className="text-[10px] uppercase tracking-widest text-brand-text2 font-bold">{label}</div>
      <div className="font-display font-bold text-2xl text-brand-dark mt-1">{value}</div>
      {subtle && <div className="text-[10px] text-brand-text2 mt-1">{subtle}</div>}
    </div>
  );
}

function AggregateTable({ title, rows, testid, idLabel }) {
  return (
    <div className="bg-white border border-brand p-4" data-testid={testid}>
      <div className="text-xs uppercase tracking-widest text-brand-text2 font-bold mb-2">{title}</div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-brand-text2 border-b border-brand">
              <th className="text-left py-1.5 pr-2">{idLabel}</th>
              <th className="text-right py-1.5 pr-2">Forsendelser</th>
              <th className="text-right py-1.5">CO2e (kg)</th>
            </tr>
          </thead>
          <tbody>
            {(!rows || rows.length === 0) && (
              <tr><td colSpan={3} className="text-center py-3 text-brand-text2">Ingen data</td></tr>
            )}
            {(rows || []).slice(0, 10).map((r, i) => (
              <tr key={i} className="border-b border-brand last:border-0">
                <td className="py-1.5 pr-2 font-mono">{(r._id || "—").toString().slice(0, 16)}</td>
                <td className="py-1.5 pr-2 text-right">{r.shipments ?? 0}</td>
                <td className="py-1.5 text-right font-semibold">{(r.co2e_kg ?? 0).toFixed(3)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DensityTable({ title, rows, testid }) {
  return (
    <div className="bg-white border border-brand p-4" data-testid={testid}>
      <div className="text-xs uppercase tracking-widest text-brand-text2 font-bold mb-2">{title}</div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-brand-text2 border-b border-brand">
              <th className="text-left py-1.5 pr-2">Rute</th>
              <th className="text-right py-1.5 pr-2">Stops</th>
              <th className="text-right py-1.5">Km</th>
            </tr>
          </thead>
          <tbody>
            {(!rows || rows.length === 0) && (
              <tr><td colSpan={3} className="text-center py-3 text-brand-text2">Ingen data</td></tr>
            )}
            {(rows || []).map((r) => (
              <tr key={r.route_id} className="border-b border-brand last:border-0">
                <td className="py-1.5 pr-2 font-mono">{(r.route_number || r.route_id || "—").slice(0, 16)}</td>
                <td className="py-1.5 pr-2 text-right">{r.shipment_count ?? 0}</td>
                <td className="py-1.5 text-right">{(r.planned_km ?? 0).toFixed(1)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================
// Vehicles
// ============================================================

function EsgVehicles() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi().get("/admin/esg/vehicles");
      setItems(data.items || []);
    } catch (e) {
      toast.error("Kunne ikke hente køretøjer");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    try {
      await adminApi().post(`/admin/esg/vehicles/${id}/approve`);
      toast.success("Køretøj godkendt");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Godkendelse fejlede");
    }
  };

  const deactivate = async (id) => {
    if (!window.confirm("Deaktiver dette køretøj?")) return;
    try {
      await adminApi().post(`/admin/esg/vehicles/${id}/deactivate`);
      toast.success("Køretøj deaktiveret");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fejl");
    }
  };

  if (loading) {
    return <div className="py-8 text-center"><Loader2 className="animate-spin text-brand-red inline" size={24}/></div>;
  }

  return (
    <div className="space-y-3" data-testid="esg-vehicles">
      <div className="flex items-center justify-between">
        <div className="text-sm text-brand-text2">{items.length} køretøj{items.length === 1 ? "" : "er"}</div>
        <button
          onClick={() => setShowCreate(true)}
          className="bg-brand-red text-white px-3 py-2 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1"
          data-testid="esg-vehicle-create-btn"
        >
          <Plus size={14}/> Nyt køretøj
        </button>
      </div>

      <div className="bg-white border border-brand overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-brand-bg2 border-b border-brand">
            <tr className="text-brand-text2 text-xs uppercase tracking-wider">
              <th className="text-left p-2.5">Nummerplade</th>
              <th className="text-left p-2.5">Type</th>
              <th className="text-left p-2.5">Ejer</th>
              <th className="text-left p-2.5">Brændstof</th>
              <th className="text-right p-2.5">g/km</th>
              <th className="text-left p-2.5">Status</th>
              <th className="text-right p-2.5">Handling</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-center text-brand-text2">Ingen køretøjer endnu.</td></tr>
            )}
            {items.map((v) => (
              <tr key={v.id} className="border-b border-brand last:border-0" data-testid={`esg-vehicle-row-${v.id}`}>
                <td className="p-2.5 font-mono font-semibold">{v.license_plate}</td>
                <td className="p-2.5">{v.vehicle_type}</td>
                <td className="p-2.5">{OWNER_LABELS[v.owner_type] || v.owner_type}</td>
                <td className="p-2.5">{FUEL_LABELS[v.fuel_type] || v.fuel_type}</td>
                <td className="p-2.5 text-right">{v.emission_factor_g_per_km ?? "—"}</td>
                <td className="p-2.5">
                  {!v.active && (
                    <span className="bg-brand-bg2 border border-brand text-brand-text2 px-2 py-0.5 text-[11px] font-semibold">Inaktiv</span>
                  )}
                  {v.active && v.approved_at && (
                    <span className="bg-green-100 text-green-800 border border-green-200 px-2 py-0.5 text-[11px] font-semibold inline-flex items-center gap-1">
                      <CheckCircle2 size={11}/> Godkendt
                    </span>
                  )}
                  {v.active && !v.approved_at && (
                    <span className="bg-amber-100 text-amber-800 border border-amber-200 px-2 py-0.5 text-[11px] font-semibold inline-flex items-center gap-1">
                      <AlertTriangle size={11}/> Afventer godkendelse
                    </span>
                  )}
                </td>
                <td className="p-2.5 text-right space-x-1">
                  {!v.approved_at && v.active && (
                    <button
                      onClick={() => approve(v.id)}
                      className="text-xs px-2 py-1 bg-green-600 text-white font-semibold"
                      data-testid={`esg-vehicle-approve-${v.id}`}
                    >
                      Godkend
                    </button>
                  )}
                  {v.active && (
                    <button
                      onClick={() => deactivate(v.id)}
                      className="text-xs px-2 py-1 border border-brand text-brand-text2"
                      data-testid={`esg-vehicle-deactivate-${v.id}`}
                    >
                      Deaktivér
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <CreateVehicleModal
          onClose={() => setShowCreate(false)}
          onDone={() => { setShowCreate(false); load(); }}
        />
      )}
    </div>
  );
}

function CreateVehicleModal({ onClose, onDone }) {
  const [form, setForm] = useState({
    license_plate: "",
    vehicle_type: "Varebil",
    owner_type: "ema",
    fuel_type: "diesel",
    emission_factor_g_per_km: "",
    emission_factor_source: "",
    notes: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.license_plate.trim()) {
      toast.error("Nummerplade er påkrævet");
      return;
    }
    setBusy(true);
    try {
      const payload = { ...form };
      if (payload.emission_factor_g_per_km === "") {
        delete payload.emission_factor_g_per_km;
      } else {
        payload.emission_factor_g_per_km = Number(payload.emission_factor_g_per_km);
      }
      await adminApi().post("/admin/esg/vehicles", payload);
      toast.success("Køretøj oprettet");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fejl");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="esg-create-modal">
      <div className="bg-white max-w-lg w-full">
        <div className="bg-brand-dark text-white px-4 py-3 flex items-center justify-between">
          <div className="font-display font-bold">Nyt ESG-køretøj</div>
          <button onClick={onClose} className="text-white/80 hover:text-white text-2xl leading-none">×</button>
        </div>
        <div className="p-4 space-y-3">
          <Field label="Nummerplade (påkrævet)" value={form.license_plate}
                  onChange={(v) => setForm({ ...form, license_plate: v.toUpperCase() })}
                  testid="esg-form-plate"/>
          <Field label="Type" value={form.vehicle_type}
                  onChange={(v) => setForm({ ...form, vehicle_type: v })}
                  testid="esg-form-type"/>
          <Select label="Ejer-type" value={form.owner_type}
                   onChange={(v) => setForm({ ...form, owner_type: v })}
                   options={[
                     { v: "ema", l: "EMA" },
                     { v: "driver", l: "Chauffør" },
                     { v: "subcontractor", l: "Vognmand" },
                   ]}
                   testid="esg-form-owner"/>
          <Select label="Brændstof" value={form.fuel_type}
                   onChange={(v) => setForm({ ...form, fuel_type: v })}
                   options={[
                     { v: "electric", l: "El" },
                     { v: "petrol", l: "Benzin" },
                     { v: "diesel", l: "Diesel" },
                     { v: "hybrid", l: "Hybrid" },
                   ]}
                   testid="esg-form-fuel"/>
          <Field label="Emissionsfaktor (g/km, valgfri)" value={form.emission_factor_g_per_km}
                  onChange={(v) => setForm({ ...form, emission_factor_g_per_km: v })}
                  testid="esg-form-factor"/>
          <Field label="Kilde for emissionsfaktor" value={form.emission_factor_source}
                  onChange={(v) => setForm({ ...form, emission_factor_source: v })}
                  testid="esg-form-source"/>
          <Field label="Noter" value={form.notes}
                  onChange={(v) => setForm({ ...form, notes: v })}
                  testid="esg-form-notes"/>
          <div className="text-[11px] text-brand-text2 italic">
            Brug aldrig udtryk som &quot;CO2-neutral&quot;, &quot;klima-neutral&quot;, &quot;grøn levering&quot;,
            &quot;zero emission&quot; — disse afvises automatisk.
          </div>
          <div className="flex gap-2 pt-3 border-t border-brand">
            <button onClick={onClose} className="flex-1 px-4 py-2 border border-brand text-brand-text2 font-semibold">Annullér</button>
            <button onClick={submit} disabled={busy} className="flex-1 px-4 py-2 bg-brand-red text-white font-bold disabled:opacity-50" data-testid="esg-form-submit">
              {busy ? <Loader2 className="inline animate-spin" size={14}/> : "Opret"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-xs text-brand-text2 uppercase tracking-wider font-semibold">{label}</label>
      <input
        className="w-full border border-brand px-3 py-2 bg-white text-sm mt-1"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
      />
    </div>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="text-xs text-brand-text2 uppercase tracking-wider font-semibold">{label}</label>
      <select
        className="w-full border border-brand px-3 py-2 bg-white text-sm mt-1"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
      >
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </div>
  );
}

// ============================================================
// Customer reports (admin)
// ============================================================

function EsgCustomerReports() {
  const [customers, setCustomers] = useState([]);
  const [period, setPeriod] = useState(currentPeriod());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await adminApi().get("/admin/b2b/customers");
        setCustomers(data.items || data || []);
      } catch (e) {
        // Some installations expose customers differently; fallback to dashboard ids
        setCustomers([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const download = async (customerId, kind) => {
    try {
      const r = await adminApi().get(
        `/admin/esg/reports/customer/${customerId}/${period}.${kind}`,
        { responseType: "blob" },
      );
      const ext = kind === "pdf" ? "pdf" : "xlsx";
      downloadBlob(r.data, `EMA-ESG_${period}_${customerId.slice(0,8)}.${ext}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download fejlede");
    }
  };

  if (loading) return <div className="py-8 text-center"><Loader2 className="animate-spin text-brand-red inline" size={24}/></div>;

  return (
    <div className="space-y-3" data-testid="esg-customer-reports">
      <div className="flex flex-wrap items-center gap-3 bg-white p-3 border border-brand">
        <label className="text-xs uppercase tracking-wider text-brand-text2 font-semibold">Periode</label>
        <select value={period} onChange={(e) => setPeriod(e.target.value)}
                  className="border border-brand px-3 py-1.5 text-sm bg-white"
                  data-testid="esg-report-period">
          {buildPeriodOptions(12).map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>

      <div className="bg-white border border-brand overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-brand-bg2 border-b border-brand">
            <tr className="text-brand-text2 text-xs uppercase tracking-wider">
              <th className="text-left p-2.5">Kunde</th>
              <th className="text-left p-2.5">CVR</th>
              <th className="text-right p-2.5">Download</th>
            </tr>
          </thead>
          <tbody>
            {customers.length === 0 && (
              <tr><td colSpan={3} className="p-6 text-center text-brand-text2">Ingen kunder fundet.</td></tr>
            )}
            {customers.map((c) => (
              <tr key={c.id} className="border-b border-brand last:border-0">
                <td className="p-2.5 font-semibold">{c.company_name}</td>
                <td className="p-2.5 font-mono text-xs">{c.cvr || "—"}</td>
                <td className="p-2.5 text-right space-x-1">
                  <button
                    onClick={() => download(c.id, "pdf")}
                    className="text-xs px-2 py-1 bg-brand-red text-white font-semibold inline-flex items-center gap-1"
                    data-testid={`esg-report-pdf-${c.id}`}
                  >
                    <Download size={12}/> PDF
                  </button>
                  <button
                    onClick={() => download(c.id, "xlsx")}
                    className="text-xs px-2 py-1 bg-brand-dark text-white font-semibold inline-flex items-center gap-1"
                    data-testid={`esg-report-xlsx-${c.id}`}
                  >
                    <Download size={12}/> Excel
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================
// MotorAPI status card (Sprint 2R-2 add-on)
// ============================================================

function MotorApiStatusCard() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  const check = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi().get("/admin/integrations/motorapi/status");
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "MotorAPI-status fejlede");
      setData({ configured: false, mock_mode: true, live_mode: false,
                 token_valid: null, message: "Kunne ikke kontakte server" });
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { check(); }, [check]);

  if (!data) {
    return (
      <div className="bg-brand-bg2 border border-brand p-3 text-xs text-brand-text2" data-testid="motorapi-status-loading">
        Tjekker MotorAPI-status...
      </div>
    );
  }

  const liveBadge = data.live_mode
    ? { cls: "bg-green-100 text-green-800 border-green-200", label: "Live-mode aktivt", Icon: CheckCircle2 }
    : data.configured
      ? { cls: "bg-amber-100 text-amber-800 border-amber-200", label: "Token sat — mock-mode aktivt", Icon: ShieldCheck }
      : { cls: "bg-slate-100 text-slate-700 border-slate-300", label: "Mock-mode (intet token)", Icon: AlertTriangle };
  const I = liveBadge.Icon;

  return (
    <div className="bg-white border border-brand p-4 flex flex-wrap items-center justify-between gap-3" data-testid="motorapi-status-card">
      <div className="flex items-start gap-3">
        <Truck size={20} className="text-brand-red mt-0.5"/>
        <div>
          <div className="text-xs uppercase tracking-widest text-brand-text2 font-bold">MotorAPI</div>
          <div className="flex items-center gap-2 mt-1">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider border ${liveBadge.cls}`} data-testid="motorapi-status-badge">
              <I size={12}/> {liveBadge.label}
            </span>
            {data.token_valid === true && (
              <span className="inline-flex items-center gap-1 text-[11px] text-green-700 font-semibold">
                <CheckCircle2 size={10}/> Token gyldigt
              </span>
            )}
            {data.token_valid === false && (
              <span className="inline-flex items-center gap-1 text-[11px] text-red-700 font-semibold">
                <AlertTriangle size={10}/> Token ugyldigt
              </span>
            )}
          </div>
          <div className="text-xs text-brand-text2 mt-1" data-testid="motorapi-status-message">{data.message}</div>
          {data.usage?.today && (
            <div className="text-[11px] text-brand-text2 mt-1" data-testid="motorapi-usage">
              I dag: {data.usage.today.requests ?? 0} af {data.usage.today.limit ?? "?"} kald brugt
              {data.usage.today.remaining != null && ` · ${data.usage.today.remaining} tilbage`}
            </div>
          )}
        </div>
      </div>
      <button
        onClick={check}
        disabled={loading}
        className="text-xs px-3 py-1.5 border border-brand text-brand-dark inline-flex items-center gap-1 disabled:opacity-50"
        data-testid="motorapi-check-btn"
      >
        <RefreshCw size={12} className={loading ? "animate-spin" : ""}/> Tjek nu
      </button>
    </div>
  );
}

